import { Injectable, EventEmitter } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { UserService } from '../auth/_services/user.service';
import { Observable, Subject, of, throwError } from 'rxjs';
import { concatMap, delay, catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
const API_URL = environment.API_URL;
const API_CHATBOT_URL = '/api/chatbot';
const API_INHOUSE_CHATBOT_URL = '/api/genai/query';
const API_INHOUSE_CHATBOT_STREAM_URL = '/api/genai/query/stream';
const API_REFRESH_URL = '/api/genai/document_upload';
const FEEDBACK_URL = '/api/genai/feedback';
const DELETE_SESSION_URL = '/api/genai/delete-session';
const API_SHARE_URL = '/api/genai/share';
const ATTACH_FILE_URL = '/genai/attach_files';
const DOCUMENT_UPLOAD_URL = '/genai/document_upload';

@Injectable({
  providedIn: 'root'
})
export class ChatbotUiService {
  chatbotText: string = "";
  systemName: string = "";
  sessionId: string = "";
  streamingComplete: boolean = false;
  PYTHON_API_URL;
  private abortController: AbortController | null = null;
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  constructor(
    private readonly http: HttpClient,
    private readonly userService: UserService,
    private readonly router: Router
  ) {
    this.PYTHON_API_URL = environment.runtime.PYTHON_API_URL;
  }

  getChatbotResponse(user: any, message: string): Observable<Object> {
    const params = { user: user, prompt: message, timezone: this.timezone };
    let url = `${API_URL}${API_CHATBOT_URL}/`;
    return this.http.get(url, { params, headers: this.userService.getHttpAuthHeader() })
  }

  refreshInhouseGpt(fileUrl: string): Observable<Object> {
    let url = `${API_URL}${API_REFRESH_URL}`;
    let params = { fileUrl: fileUrl };
    return this.http.get(url, { params, headers: this.userService.getHttpAuthHeader() });
  }

  sendFeedback(run_id: string, feedback: number, description: string = '',category: string = null): Observable<Object> {
    const url = `${API_URL}${FEEDBACK_URL}`;
    const headers = this.userService.getHttpAuthHeader();
    const body = {
      run_id,
      type: feedback ,
      description,
      category
    };
    return this.http.post(url, body, { headers });
  }

  private isGenaiModeEnabled(): boolean {
    const currentUrl = this.router.url;
    return this.router.parseUrl(currentUrl).queryParamMap.get('genai') === '1';
  }

  private getInhouseChatbotUrl(streaming: boolean = false): string {
    if (this.isGenaiModeEnabled()) {
      const endpoint = streaming ? '/genai/query_stream' : '/genai/query';
      return `${this.PYTHON_API_URL}${endpoint}`;
    }

    const endpoint = streaming ? API_INHOUSE_CHATBOT_STREAM_URL : API_INHOUSE_CHATBOT_URL;
    return `${API_URL}${endpoint}`;
  }
  stopStreaming(): void {
    this.abortController?.abort();
    this.abortController = null;
    this.streamingComplete = true;
  }

  // Backend currently only supports POST with body; default method is POST so every caller
  // hits the POST path. The GET branch is kept for if/when GET is re-enabled in the future.
  getInhouseChatbotResponse(message: string, streaming: boolean, createdTime: string = null, method = "POST", body = {}): Observable<any> {
    this.streamingComplete = false;
    const url = this.getInhouseChatbotUrl(streaming);
    const userId: string = JSON.parse(localStorage.getItem('user'))?._id?.toString();
    const params = new HttpParams()
      .set('session_id', this.sessionId)
      .set('query', message)
      .set('user_id', userId)
      .set('createdTime', createdTime)
      .set('timezone', this.timezone);
    if (streaming) {
      const headers = this.userService.getHttpAuthHeader().set('Content-Type', 'application/json');
      const headersObj: { [key: string]: string } = {};
      headers.keys().forEach((key) => {
        headersObj[key] = headers.get(key) || '';
      });

      const subject = new Subject<any>();
      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;
      const settle = () => {
        if (this.abortController === controller) this.abortController = null;
        this.streamingComplete = true;
        subject.complete();
      };
      let payload = {
        method: method,
        headers: headersObj,
        signal: controller.signal,
      }
      if (method === 'POST') {
        body['session_id'] = this.sessionId;
        body['createdTime'] = createdTime;
        payload['body'] = JSON.stringify(body);
      }
      const fetchUrl = method === 'POST' ? url : `${url}?${params.toString()}`;
      fetch(fetchUrl, payload)
        .then(async (response) => {
          if (!response.ok) {
            if (response.status === 401) {
              this.router.navigate(['/web/login'], {
                queryParams: { returnUrl: window.location.pathname },
              });
            }
            subject.error(response);
            return;
          }

          const reader = response.body?.getReader();
          if (!reader) {
            const errorMessage = 'ReadableStream not supported by the browser.';
            subject.error(errorMessage);
            return;
          }

          const decoder = new TextDecoder();
          let buffer = '';

          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) {
                this.streamingComplete = true;
                if (buffer.trim() && buffer.trim() !== 'data: [DONE]') {
                  try {
                    const cleaned = buffer.trim().startsWith('data:')
                      ? buffer.trim().replace(/^data:\s*/, '')
                      : buffer.trim();
                    const jsonObj = JSON.parse(cleaned);
                    subject.next(jsonObj);
                  } catch (error) {
                    subject.error('Error parsing final chunk of data.');
                  }
                }
                break;
              }

              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';

              for (const line of lines) {
                const trimmed = line.trim();
                if (!trimmed) continue;

                if (trimmed === 'data: [DONE]') {
                  settle();
                  return;
                }

                try {
                  const cleaned = trimmed.startsWith('data:')
                    ? trimmed.replace(/^data:\s*/, '')
                    : trimmed;
                  const jsonChunk = JSON.parse(cleaned);
                  subject.next(jsonChunk);
                } catch (error) {
                  subject.error('Error parsing a chunk of streaming data.');
                }
              }
            }
            settle();
          } catch (error) {
            if (controller.signal.aborted) settle();
            else subject.error('Error during streaming response processing.');
          }
        })
        .catch((error) => {
          if (controller.signal.aborted) settle();
          else subject.error('Error fetching streaming response.');
        });

      return subject.asObservable().pipe(
        concatMap((chunk) => of(chunk).pipe(delay(0)))
      );
    } else {
      const headers = this.userService.getHttpAuthHeader();
      let request$: Observable<Object>;
      if (method === 'POST') {
        body['session_id'] = this.sessionId;
        request$ = this.http.post(url, body, { params, headers });
      } else {
        request$ = this.http.get(url, { params, headers });
      }
      return request$.pipe(
        tap(() => {
          this.streamingComplete = true;
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            this.router.navigate(['/web/login'], {
              queryParams: { returnUrl: window.location.pathname },
            });
          }
          return throwError(() => error);
        })
      );
    }
  }

  getSharedChat(runId:string): Observable<Object> {
    let url = `${API_URL}${API_SHARE_URL}`;
    let params = new HttpParams().set('run_id', runId);
    return this.http.get(url, {params, headers: this.userService.getHttpAuthHeader() });
  }

  createNewSessionId() {
    const currentTimeInMilli = Date.now().toString();
    let userId: string = JSON.parse(localStorage.getItem('user'))?._id?.toString();
    this.sessionId = currentTimeInMilli + (userId ? userId.slice(-4) : "");
  }

  // For attachFiles API commented the previous implementation and added a new one with document_upload api - may be in future this might be needed
  // uploadFiles(files: any[]): Observable<Object> {
  //   let url = `${this.PYTHON_API_URL}${ATTACH_FILE_URL}`;
  //   const body = {
  //     session_id: this.sessionId,
  //     files: files.map(file => ({
  //       name: file['uniqueName'] || file.name,
  //       url: file.url
  //     }))
  //   };
  //   return this.http.post(url, body, { headers: this.userService.getHttpAuthHeader() });
  // }


  // New implementation for document_upload api
  uploadFiles(files: any[], includeChatSessionId: boolean = false): Observable<Object> {
    const url = `${this.PYTHON_API_URL}${DOCUMENT_UPLOAD_URL}`;
    const fileIds = files.map(file => file._id );
    const body: any = {
      _ids: fileIds
    };
    if (includeChatSessionId) {
      if (!this.sessionId) this.createNewSessionId();
      body.session_id = this.sessionId;
    }

    return this.http.post(url, body, { headers: this.userService.getHttpAuthHeader() });
  }

  deleteSession(): Observable<Object> {
    let url = `${this.PYTHON_API_URL}${DELETE_SESSION_URL}`;
    const headers = this.userService.getHttpAuthHeader();
    const params = { session_id: this.sessionId }
    return this.http.get(url, { params, headers });
  }
}
